import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHome
          ? 'bg-background-50/80 backdrop-blur-md border-b border-background-200/60'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="text-lg md:text-xl font-heading font-bold text-foreground-950 tracking-tight whitespace-nowrap"
          >
            allchinabuy<span className="text-primary-500">.info</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap px-3 py-2 rounded-lg hover:bg-primary-50"
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Home
            </Link>

            <Link
              to="/guide"
              className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap px-3 py-2 rounded-lg hover:bg-primary-50"
            >
              <i className="ri-book-read-line mr-1.5"></i>
              Shopping Guide
            </Link>

            {/* Categories dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap px-3 py-2 rounded-lg hover:bg-primary-50 cursor-pointer"
                aria-expanded={dropdownOpen}
              >
                <i className="ri-grid-line mr-1"></i>
                Categories
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-background-50 rounded-xl border border-background-200 py-2 shadow-lg z-50 max-h-[60vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <i className="ri-arrow-right-up-line text-primary-400 text-xs"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA buttons */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
            >
              AllChinaBuy Sheet
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer rounded-lg hover:bg-background-100"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-4 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1 px-2">
              <Link
                to="/"
                className="flex items-center gap-2.5 text-sm font-medium text-foreground-900 py-3 px-3 rounded-lg hover:bg-primary-50 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-4-line text-lg"></i>
                Home
              </Link>
              <Link
                to="/guide"
                className="flex items-center gap-2.5 text-sm font-medium text-foreground-900 py-3 px-3 rounded-lg hover:bg-primary-50 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-book-read-line text-lg"></i>
                Shopping Guide
              </Link>

              {/* Mobile categories accordion */}
              <div className="border-t border-background-200 mt-2 pt-2">
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="flex items-center justify-between w-full text-sm font-medium text-foreground-900 py-3 px-3 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2.5">
                    <i className="ri-grid-line text-lg"></i>
                    Categories
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileCatOpen && (
                  <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-1.5 text-sm text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <i className="ri-arrow-right-up-line text-primary-400 text-xs"></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mt-3"
              >
                AllChinaBuy Sheet
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}